import { useState, useEffect } from "react";
import { format } from "date-fns";
import { Phone, Mail, Calendar, MapPin, Clock, CreditCard, FileText, MessageCircle, Send } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { useTranslation } from "react-i18next";

// Custom WhatsApp and Telegram icons
const MessagingIcon = ({ platform }: { platform: string }) => {
  if (platform === 'telegram') {
    return (
      <img 
        src="/icons8-telegram.svg" 
        alt="Telegram" 
        className="w-4 h-4" 
        style={{ filter: 'brightness(0) invert(1)' }}
      />
    );
  } else {
    return (
      <img 
        src="/icons8-whatsapp.svg" 
        alt="WhatsApp" 
        className="w-4 h-4" 
        style={{ filter: 'brightness(0) invert(1)' }}
      />
    );
  }
};

type ServiceHistoryItem = {
  id: string;
  service_name: string;
  service_price: number;
  status: string;
  created_at: string;
  scheduled_at: string | null;
  paid: boolean;
  notes: string | null;
  location: string | null;
};

interface CustomerDetailsSheetProps {
  customer: {
    name: string;
    phone_number: string;
    email: string | null;
    last_request_date: string;
  } | null;
  isOpen: boolean;
  onClose: () => void;
}

export const CustomerDetailsSheet = ({ customer, isOpen, onClose }: CustomerDetailsSheetProps) => {
  const [serviceHistory, setServiceHistory] = useState<ServiceHistoryItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [preferredMessaging, setPreferredMessaging] = useState("whatsapp");
  const { t } = useTranslation();

  useEffect(() => {
    if (isOpen && customer) {
      fetchServiceHistory();
      fetchUserPreferences();
    }
  }, [isOpen, customer]);

  const fetchServiceHistory = async () => {
    if (!customer) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error("User not authenticated");
      }

      const { data, error } = await supabase.rpc(
        'get_customer_service_history',
        { 
          p_user_id: user.id,
          p_phone_number: customer.phone_number
        }
      );

      if (error) throw error;
      setServiceHistory(data || []);
    } catch (err) {
      console.error('Error fetching service history:', err);
      setError(err instanceof Error ? err.message : "Failed to load service history");
    } finally {
      setLoading(false);
    }
  };

  const fetchUserPreferences = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data: profile } = await supabase
        .from('profiles')
        .select('preferred_messaging')
        .eq('id', user.id)
        .single();

      if (profile?.preferred_messaging) {
        setPreferredMessaging(profile.preferred_messaging);
      }
    } catch (error) {
      console.error('Error fetching user preferences:', error);
    }
  };

  const handleContactCustomer = () => {
    if (!customer) return;
    
    // Format phone number (ensure it starts with country code and has no special characters)
    let phoneNumber = customer.phone_number.replace(/\s/g, '');
    
    // Ensure it has a + if needed
    if (!phoneNumber.startsWith('+')) {
      // Add default country code (e.g., +254 for Kenya) if missing
      phoneNumber = '+' + phoneNumber;
    }
    
    // Remove the + for the URL
    const formattedNumber = phoneNumber.replace(/^\+/, '');
    
    if (preferredMessaging === 'telegram') {
      // For Telegram, we need to use a universal link that works with the app
      // This will open the Telegram app if installed, or web version as fallback
      window.open(`https://telegram.me/+${formattedNumber}`, '_blank', 'noopener,noreferrer');
    } else {
      // For WhatsApp, use the standard API with formatted number
      window.open(`https://api.whatsapp.com/send?phone=${formattedNumber}`, '_blank', 'noopener,noreferrer');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'accepted':
        return 'bg-blue-100 text-blue-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'rejected':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <Sheet open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <SheetContent className="overflow-y-auto sm:max-w-lg bg-white">
        <SheetHeader>
          <SheetTitle className="text-gebeya-pink">{t('customers.customerDetails')}</SheetTitle>
        </SheetHeader>
        
        {customer && (
          <div className="mt-6 space-y-6">
            {/* Customer Info Section */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex justify-between items-center">
                  <span>{customer.name}</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <div className="flex items-center gap-2">
                  <Phone className="h-4 w-4 text-gray-500" />
                  <span>{customer.phone_number}</span>
                </div>
                {customer.email && (
                  <div className="flex items-center gap-2">
                    <Mail className="h-4 w-4 text-gray-500" />
                    <span>{customer.email}</span>
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-gray-500" />
                  <span>{t('customers.lastRequest')}: {format(new Date(customer.last_request_date), "PPP")}</span>
                </div>
                
                <div className="pt-3 mt-3 border-t border-gray-100">
                  <Button
                    size="sm"
                    variant="default"
                    className={`flex items-center gap-1 w-full text-white ${
                      preferredMessaging === 'telegram' 
                        ? 'bg-[#0088CC] hover:bg-[#0077a6]' 
                        : 'bg-[#25D366] hover:bg-[#128C7E]'
                    }`}
                    onClick={handleContactCustomer}
                  >
                    {preferredMessaging === 'telegram' ? (
                      <>
                        <MessagingIcon platform="telegram" />
                        <span>{t('customers.contact')}</span>
                      </>
                    ) : (
                      <>
                        <MessagingIcon platform="whatsapp" />
                        <span>{t('customers.contact')}</span>
                      </>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Service History Section */}
            <div>
              <h3 className="text-md font-medium mb-3">{t('customers.serviceRequestHistory')}</h3>
              
              {loading ? (
                <div className="flex justify-center py-6">
                  <img 
                    src="/lovable-uploads/f8714beb-a432-4da6-acab-3ef6f0a42367.png" 
                    alt="Gebeya Logo" 
                    className="h-8 w-8 gebeya-spinner" 
                  />
                </div>
              ) : error ? (
                <div className="text-red-500 text-center py-4">{error}</div>
              ) : serviceHistory.length === 0 ? (
                <div className="text-gray-500 text-center py-4">{t('customers.noServiceHistory')}</div>
              ) : (
                <div className="space-y-4">
                  {serviceHistory.map((item) => (
                    <Card key={item.id} className="overflow-hidden">
                      <div className="border-l-4 border-gebeya-pink px-4 py-3">
                        <div className="flex justify-between">
                          <h4 className="font-medium">{item.service_name}</h4>
                          <Badge className={getStatusColor(item.status)}>
                            {t(`status.${item.status}`)}
                          </Badge>
                        </div>
                        <div className="text-sm text-gray-500 mt-1">
                          {t('customers.price')}: {item.service_price.toFixed(2)}
                        </div>
                      </div>
                      <CardContent className="pt-3">
                        <div className="grid grid-cols-1 gap-2 text-sm">
                          <div className="flex items-start gap-2">
                            <Calendar className="h-4 w-4 text-gray-500 mt-0.5" />
                            <div>
                              <div className="text-gray-600">{t('customers.createdOn')}</div>
                              <div>{format(new Date(item.created_at), "PPp")}</div>
                            </div>
                          </div>
                          
                          {item.scheduled_at && (
                            <div className="flex items-start gap-2">
                              <Clock className="h-4 w-4 text-gray-500 mt-0.5" />
                              <div>
                                <div className="text-gray-600">{t('customers.scheduledFor')}</div>
                                <div>{format(new Date(item.scheduled_at), "PPp")}</div>
                              </div>
                            </div>
                          )}
                          
                          {item.location && (
                            <div className="flex items-start gap-2">
                              <MapPin className="h-4 w-4 text-gray-500 mt-0.5" />
                              <div>
                                <div className="text-gray-600">{t('customers.location')}</div>
                                <div>{item.location}</div>
                              </div>
                            </div>
                          )}
                          
                          <div className="flex items-center gap-2">
                            <CreditCard className="h-4 w-4 text-gray-500" />
                            <span className={item.paid ? "text-green-600" : "text-red-600"}>
                              {item.paid ? t('customers.paid') : t('customers.notPaid')}
                            </span>
                          </div>
                          
                          {item.notes && (
                            <div className="flex items-start gap-2 mt-1">
                              <FileText className="h-4 w-4 text-gray-500 mt-0.5" />
                              <div>
                                <div className="text-gray-600">{t('customers.notes')}</div>
                                <div>{item.notes}</div>
                              </div>
                            </div>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};
